import React, { FunctionComponent, useEffect, useState } from "react";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DropdownChangeParams } from "primereact/dropdown";
import { ICommonComponentEditorProps } from "../../ComponentType";
import { DatasetLoadingConf, IPIDControllerProps } from "../charts/interfaces";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";

const DefaultDatasetLoadingConf: DatasetLoadingConf = {
  lead: 0,
  lag: 6,
  lagUnit: "HOURS",
  leadUnit: "HOURS",
  alignUnit: null,
};

const PIDControllerEditor: FunctionComponent<ICommonComponentEditorProps> = ({
  config,
  onConfigChange,
}) => {
  const conf: IPIDControllerProps = config ?? {};
  const [currentConfig, setCurrentConfig] = useState(conf);

  const handleTextChange = (name: keyof IPIDControllerProps, value: string) => {
    (conf[name] as string) = value;
    setCurrentConfig({ ...conf });
  };

  const handleNumberChange = (name: keyof IPIDControllerProps, value: number | null) => {
    (conf[name] as string | number) = value ?? "";
    setCurrentConfig({ ...conf });
  };
  const handleLoadConf = (e: InputSwitchChangeParams): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadConf = defaultLoadConf;
    } else {
      delete tempConfig.loadConf;
    }
    setCurrentConfig(tempConfig);
  };

  const handleLoadConfChange = (e: InputNumberValueChangeParams | DropdownChangeParams) => {
    const key = e.target.id as keyof DatasetLoadingConf;
    let { value } = e.target;
    if (Number.isNaN(value)) value = value.toString().toUpperCase();
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: prev.loadConf
        ? { ...prev.loadConf, [key]: value }
        : { ...DefaultDatasetLoadingConf, [key]: value },
    }));
  };

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  return (
    <div className="flex flex-column flex-wrap gap-3">
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="kp">K Proportional</label>
          <InputText
            id="kp"
            placeholder="Dataset ID"
            value={conf.kp}
            onChange={e => handleTextChange("kp", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="ki">K Integral</label>
          <InputText
            id="ki"
            placeholder="Dataset ID"
            value={conf.ki}
            onChange={e => handleTextChange("ki", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="kd">K Derivative</label>
          <InputText
            id="kd"
            placeholder="Dataset ID"
            value={conf.kd}
            onChange={e => handleTextChange("kd", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="kall">K all</label>
          <InputText
            id="kall"
            placeholder="Dataset ID"
            value={conf.kall}
            onChange={e => handleTextChange("kall", e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-column w-3">
        <label htmlFor="delta">Δ</label>
        <InputText
          id="delta"
          placeholder="Dataset ID"
          value={conf.delta}
          onChange={e => handleTextChange("delta", e.target.value)}
        />
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="controlVariable">Control Variable</label>
          <InputText
            id="controlVariable"
            placeholder="Dataset ID"
            value={conf.controlVariable}
            onChange={e => handleTextChange("controlVariable", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="sp">Set Point</label>
          <InputText
            id="sp"
            placeholder="Dataset ID"
            value={conf.setPoint}
            onChange={e => handleTextChange("setPoint", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="mp">Manipulated Variable</label>
          <InputText
            id="mp"
            placeholder="Dataset ID"
            value={conf.manipulatedVariable}
            onChange={e => handleTextChange("manipulatedVariable", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="min">Min</label>
          <InputText
            id="min"
            placeholder="Dataset ID"
            value={conf.min}
            onChange={e => handleTextChange("min", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="max">Max</label>
          <InputText
            id="max"
            placeholder="Dataset ID"
            value={conf.max}
            onChange={e => handleTextChange("max", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="updateFrequency">Update Frequency (seconds)</label>
          <InputNumber
            id="updateFrequency"
            placeholder="Seconds"
            value={conf.updateFrequency}
            onValueChange={e => handleNumberChange("updateFrequency", e.value)}
            mode="decimal"
          />
        </div>
      </div>
      <div className="flex flex-row">
        <div className="gap-2 flex flex-column">
          <label htmlFor="loadConf">Load Conf</label>
          <InputSwitch
            id="loadConf"
            checked={currentConfig.loadConf !== undefined}
            onChange={handleLoadConf}
          />
        </div>
        {currentConfig.loadConf !== undefined && (
          <>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">Lag</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={currentConfig.loadConf.lag as number}
                  onValueChange={handleLoadConfChange}
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.lagUnit}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">Lead</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={currentConfig.loadConf.lead as number}
                  onValueChange={handleLoadConfChange}
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.leadUnit}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">Align Unit</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={currentConfig.loadConf.alignUnit ? currentConfig.loadConf.alignUnit : ""}
                onChange={handleLoadConfChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PIDControllerEditor;
